import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Badge } from "@/components/ui/badge"
import { 
  LayoutDashboard, 
  Users, 
  Camera, 
  UserCheck, 
  Handshake, 
  Mail, 
  Users2, 
  Building, 
  Calendar,
  Building2,
  Bell,
  Image,
  LogOut,
  Package,
  Coins,
  Map,
  FileText,
  HelpCircle,
  Palette,
  TrendingUp,
  AlertTriangle,
  Eye,
  Factory,
  Wrench,
  Key
} from "lucide-react"
import { Stethoscope, Layers, HeartHandshake } from "lucide-react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useCrmAccess } from "@/hooks/use-crm-access"
import { useFullCrmAccess } from "@/hooks/use-full-crm-access"
import { usePartnerCrmAccess } from "@/hooks/use-partner-crm-access"
import { useEnsurePartnerLink } from "@/hooks/use-ensure-partner-link"
import { useSyncPartnerProfile } from "@/hooks/use-sync-partner-profile"
import { supabase } from "@/integrations/supabase/client"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useAuth } from "@/contexts/AuthContext"
import { useEnhancedCreditPackages } from "@/hooks/use-enhanced-credit-packages"
import { useIsMobile } from "@/hooks/use-mobile"
import { useUnreadSystemNotificationsCount } from "@/hooks/use-system-notifications"
import { useUnseenOutageCount } from "@/hooks/use-outage-notifications"
import { usePendingPartnerContactsCount } from "@/hooks/use-pending-partner-contacts-count"
import { useBrand } from "@/contexts/BrandContext"
import { getBrandLogoStyle } from "@/lib/brand/logoScale"
import { useSubOrgContext } from "@/hooks/use-sub-organizations"
import { useOrgAdminFeatures } from "@/hooks/use-org-admin-features"

// Menu items for different sections
const mainItems = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
    mobileVisible: true,
  },
  {
    title: "Map",
    url: "/dashboard/map",
    icon: Map,
    mobileVisible: true,
  },
  {
    title: "Gallery",
    url: "/dashboard/gallery",
    icon: Image,
    mobileVisible: false,
  },
  {
    title: "Profile",
    url: "/dashboard/profile",
    icon: Users,
    mobileVisible: true,
  },
  {
    title: "Help",
    url: "/documentation",
    icon: HelpCircle,
    mobileVisible: false,
  },
  {
    title: "Info",
    url: "/info",
    icon: FileText,
    mobileVisible: false,
  },
]

const adminItems = [
  {
    title: "Orders",
    url: "/dashboard/admin/orders",
    icon: Package,
    mobileVisible: true,
  },
  {
    title: "Notifications",
    url: "/dashboard/notifications",
    icon: Bell,
    badge: true,
    mobileVisible: true,
  },
  {
    title: "User Management",
    url: "/dashboard/admin",
    icon: UserCheck,
    mobileVisible: true,
  },
  {
    title: "Credit Management",
    url: "/dashboard/credit-management",
    icon: Coins,
    mobileVisible: false,
  },
  {
    title: "Customer Management",
    url: "/dashboard/sub-customers",
    icon: Building2,
    mobileVisible: false,
  },
  {
    title: "Device Diagnosis",
    url: "/dashboard/device-diagnosis",
    icon: Stethoscope,
    mobileVisible: false,
  },
  {
    title: "Organisation Profile",
    url: "/dashboard/organisation-profile",
    icon: Building2,
    mobileVisible: false,
  },
  {
    title: "API Configuration",
    url: "/dashboard/api-configuration",
    icon: Key,
    mobileVisible: false,
  },
]

const superAdminItems = [
  {
    title: "User Roles",
    url: "/dashboard/user-roles",
    icon: UserCheck,
    mobileVisible: true,
  },
  {
    title: "Device Details",
    url: "/dashboard/device-details",
    icon: Camera,
    mobileVisible: true,
  },
  {
    title: "Device Groups",
    url: "/dashboard/device-groups",
    icon: Building,
    mobileVisible: false,
  },
  {
    title: "Device Assignments",
    url: "/dashboard/device-assignments",
    icon: UserCheck,
    mobileVisible: true,
  },
  {
    title: "Bulk Update",
    url: "/dashboard/bulk-update",
    icon: Layers,
    mobileVisible: false,
  },
  {
    title: "Customer Success",
    url: "/dashboard/admin/customer-success",
    icon: HeartHandshake,
    mobileVisible: false,
    superuserOnly: true,
  },
  {
    title: "Outage Management",
    url: "/dashboard/outages/manage",
    icon: AlertTriangle,
    mobileVisible: false,
  },
  {
    title: "Email Design",
    url: "/dashboard/design",
    icon: Palette,
    mobileVisible: false,
  },
  {
    title: "Brand Studio",
    url: "/dashboard/admin/white-label",
    icon: Palette,
    mobileVisible: false,
  },
  {
    title: "Website",
    url: "/",
    icon: Eye,
    mobileVisible: false,
  },
]

const productionItems = [
  {
    title: "Assembly",
    url: "/dashboard/assembly",
    icon: Wrench,
    mobileVisible: true,
  },
  {
    title: "Quality Control",
    url: "/dashboard/production",
    icon: Factory,
    mobileVisible: true,
  },
]

// CRM items with Orders added after Opportunities
const crmItems = [
  {
    title: "Staff",
    url: "/dashboard/crm/staff",
    icon: Users2,
    mobileVisible: false,
  },
  {
    title: "Contacts",
    url: "/dashboard/crm/contacts", 
    icon: Mail,
    mobileVisible: true,
  },
  {
    title: "Partners", 
    url: "/dashboard/crm/partners",
    icon: Handshake,
    mobileVisible: false,
  },
  {
    title: "Organisations",
    url: "/dashboard/crm/companies",
    icon: Building2,
    mobileVisible: true,
  },
  {
    title: "Opportunities",
    url: "/dashboard/crm/deals",
    icon: Handshake,
    mobileVisible: true,
  },
  {
    title: "Orders",
    url: "/dashboard/crm/orders",
    icon: Package,
    mobileVisible: true,
  },
  {
    title: "Activities",
    url: "/dashboard/crm/activities",
    icon: Calendar,
    mobileVisible: true,
  },
]

const businessPerformanceItems = [
  {
    title: "Business KPIs",
    url: "/dashboard/crm/kpi",
    icon: TrendingUp,
    mobileVisible: false,
  },
  {
    title: "Pipeline",
    url: "/dashboard/crm/pipeline",
    icon: Calendar,
    mobileVisible: false,
  },
  {
    title: "Marketing Calendar",
    url: "/dashboard/crm/marketing-calendar",
    icon: Calendar,
    mobileVisible: false,
  },
]

export function AppSidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { signOut, user } = useAuth()
  const { data: hasCrmAccess } = useCrmAccess()
  const { data: hasFullCrmAccess } = useFullCrmAccess()
  const { data: isPartner } = usePartnerCrmAccess()
  const { state } = useSidebar()
  const isMobile = useIsMobile()
  const { brand, isWhiteLabel } = useBrand()
  const logoUrl = isWhiteLabel && brand?.config.logo_url
    ? brand.config.logo_url
    : "/lovable-uploads/68969e6c-4c4a-48d9-ab2a-7f964de5d68f.png"
  const logoAlt = isWhiteLabel ? `${brand?.config.company_name || "Partner"} logo` : "Snapshot Live"

  // Ensure partner linking and profile syncing globally
  useEnsurePartnerLink()
  useSyncPartnerProfile()

  // Check user role for different access levels
  const { data: userRole } = useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase.rpc('get_user_role', {
        user_id: user.id
      });
      return data;
    },
    enabled: !!user?.id,
  });

  const isAdmin = userRole === 'admin' || userRole === 'superuser';
  const isSuperAdmin = userRole === 'superuser' || userRole === 'internal_staff';
  const hasBusinessPerformance = userRole === 'superuser' || userRole === 'advisor';
  const isProductionRole = userRole === 'production';
  const { isEnabled: isOrgFeatureEnabled } = useOrgAdminFeatures();
  const featureByUrl: Record<string, 'customer_admin' | 'device_diagnosis' | 'api_configuration'> = { '/dashboard/sub-customers': 'customer_admin', '/dashboard/device-diagnosis': 'device_diagnosis', '/dashboard/api-configuration': 'api_configuration' };
  const showProductionGroup = isSuperAdmin || isProductionRole;

  // Pending partner-submitted contacts awaiting staff approval.
  // Skip for partner users (they don't approve their own submissions).
  const { data: pendingPartnerContactsCount = 0 } = usePendingPartnerContactsCount(
    !!hasFullCrmAccess && !isPartner
  );

  // Get user's organization for credit package filtering
  const { data: userOrganization } = useQuery({
    queryKey: ['user-organization', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('user_roles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();
      return data?.organization_id || null;
    },
    enabled: !!user?.id,
  });

  // Get unallocated credit packages count
  const { data: allCreditPackages = [] } = useEnhancedCreditPackages();
  const unallocatedCount = allCreditPackages.filter(pkg => 
    userOrganization && 
    pkg.organization_id === userOrganization &&
    pkg.assignment_type === 'user_owned' && 
    !pkg.is_assigned
  ).length;

  // Get system notifications count
  const { data: systemNotificationCount = 0 } = useUnreadSystemNotificationsCount();

  // Get unseen outage notifications count
  const { data: unseenOutageCount = 0 } = useUnseenOutageCount();

  // Enhanced notification count query (proposal + organization profile notifications)
  const { data: notificationCount = 0 } = useQuery({
    queryKey: ["notification-count"],
    queryFn: async (): Promise<number> => {
      try {
        console.log("🔔 [Sidebar] Starting notification count query");
        
        if (!user) {
          console.log("🔔 [Sidebar] No authenticated user");
          return 0;
        }

        console.log("🔔 [Sidebar] Fetching notification count for user:", user.email);

        let totalCount = 0;

        // Count proposal notifications
        // First try: Direct query using user_id as contact_id
        console.log("🔔 [Sidebar] Trying direct user_id lookup");
        const { count: directCount, error: directError } = await supabase
          .from('proposal_notifications')
          .select('*', { count: 'exact' })
          .eq('contact_id', user.id)
          .eq('status', 'pending');

        if (!directError && directCount !== null && directCount > 0) {
          console.log("✅ [Sidebar] Found proposal notifications via direct user_id:", directCount);
          totalCount += directCount;
        } else {
          // Second try: Find profile by user_id and use that as contact_id
          console.log("🔔 [Sidebar] Trying profile lookup by user_id");
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (!profileError && profile?.id) {
            console.log("✅ [Sidebar] Found profile by user_id:", profile.id);
            
            const { count, error: countError } = await supabase
              .from('proposal_notifications')
              .select('*', { count: 'exact' })
              .eq('contact_id', profile.id)
              .eq('status', 'pending');

            if (!countError && count !== null) {
              console.log("📊 [Sidebar] Proposal notification count via profile:", count);
              totalCount += count;
            }
          } else {
            // Third try: Email fallback
            console.log("⚠️ [Sidebar] Trying email fallback");
            const { data: emailProfile, error: emailError } = await supabase
              .from('profiles')
              .select('id')
              .eq('email', user.email)
              .maybeSingle();

            if (!emailError && emailProfile?.id) {
              console.log("✅ [Sidebar] Found profile by email:", emailProfile.id);
              
              const { count, error: countError } = await supabase
                .from('proposal_notifications')
                .select('*', { count: 'exact' })
                .eq('contact_id', emailProfile.id)
                .eq('status', 'pending');

              if (!countError && count !== null) {
                console.log("📊 [Sidebar] Proposal notification count via email fallback:", count);
                totalCount += count;
              }
            }
          }
        }

        // Count organization profile completion notifications
        const { count: orgCount, error: orgError } = await supabase
          .from('organization_profile_notifications')
          .select('*', { count: 'exact' })
          .eq('user_id', user.id)
          .eq('status', 'pending');

        if (!orgError && orgCount !== null) {
          console.log("📊 [Sidebar] Organization profile notification count:", orgCount);
          totalCount += orgCount;
        }

        console.log("📊 [Sidebar] Total notification count:", totalCount);
        return totalCount;
        
      } catch (error) {
        console.error("❌ [Sidebar] Unexpected error in notification count query:", error);
        return 0;
      }
    },
    refetchInterval: 30000,
    retry: 1,
    staleTime: 5000,
    select: (data) => typeof data === 'number' ? data : 0,
    enabled: !!user
  });

  // Calculate total notification count including system notifications and outages
  const totalNotificationCount = notificationCount + systemNotificationCount + unseenOutageCount;

  const queryClient = useQueryClient();

  const handleLogout = async () => {
    try {
      // CRITICAL: Clear ALL cached data before signing out
      // This prevents cross-user data leakage when a new user logs in
      queryClient.clear();
      
      await signOut();
      
      // Add small delay to ensure localStorage is fully cleared
      await new Promise(resolve => setTimeout(resolve, 100));
      
      navigate("/", { state: { justLoggedOut: true } });
      toast.success("Logged out successfully");
    } catch (error) {
      toast.error("Error logging out");
      console.error("Error:", error);
    }
  };

  const isCollapsed = state === "collapsed";

  const subOrgCtx = useSubOrgContext()

  const renderMenuItem = (item: any) => {
    // Skip superuser-only items if user is not superuser
    if (item.superuserOnly && !isSuperAdmin) {
      return null;
    }
    
    // Skip items not visible on mobile
    if (isMobile && !item.mobileVisible) {
      return null;
    }

    const isContactsItem = item.url === "/dashboard/crm/contacts";
    const showPendingBadge = isContactsItem && pendingPartnerContactsCount > 0;
    const linkUrl = item.url;

    const content = (
      <SidebarMenuButton 
        asChild 
        isActive={location.pathname === item.url}
        className="brand-nav-item text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      >
        <Link to={linkUrl} className="flex items-center gap-2">
          <span className="relative inline-flex">
            <item.icon className="h-4 w-4" />
            {isCollapsed && showPendingBadge && (
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
            )}
          </span>
          {!isCollapsed && <span>{item.title}</span>}
          {!isCollapsed && showPendingBadge && (
            <Badge
              variant="secondary"
              className="ml-auto bg-red-100 text-red-700 text-xs"
            >
              {pendingPartnerContactsCount}
            </Badge>
          )}
              {!isCollapsed && item.badge && totalNotificationCount > 0 && (
                <Badge 
                  variant="secondary" 
                  className="ml-auto bg-red-100 text-red-700 text-xs"
                >
                  {totalNotificationCount}
                </Badge>
              )}
          {!isCollapsed && item.title === "Credit Management" && unallocatedCount > 0 && (
            <Badge 
              variant="secondary" 
              className="ml-auto bg-green-100 text-green-700 text-xs"
            >
              {unallocatedCount}
            </Badge>
          )}
        </Link>
      </SidebarMenuButton>
    );

    if (isCollapsed) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {content}
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{item.title}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return content;
  };

  return (
    <Sidebar className="brand-sidebar border-r border-sidebar-border bg-sidebar" collapsible="icon">
      <div className="brand-sidebar-logo flex items-center justify-center p-4 border-b border-sidebar-border h-30">
        {!isCollapsed && <img src={logoUrl} alt={logoAlt} className={isWhiteLabel ? "brand-sidebar-logo-image object-contain" : "h-12 max-w-[11rem] object-contain"} style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined} />}
      </div>
      
      <SidebarContent>
        {!isProductionRole && (
        <SidebarGroup>
          {!isCollapsed && <SidebarGroupLabel className="brand-nav-label">Main</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  {renderMenuItem(item)}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}

        {isAdmin && !isProductionRole && (
          <SidebarGroup>
            {!isCollapsed && <SidebarGroupLabel className="brand-nav-label">Admin</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {adminItems.filter((item) => !featureByUrl[item.url] || isOrgFeatureEnabled(featureByUrl[item.url])).map((item) => (
                  <SidebarMenuItem key={item.title}>
                    {renderMenuItem(item)}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {showProductionGroup && (
          <SidebarGroup>
            {!isCollapsed && <SidebarGroupLabel className="brand-nav-label">Production</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {productionItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    {renderMenuItem(item)}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {isSuperAdmin && !isProductionRole && (
          <SidebarGroup>
            {!isCollapsed && <SidebarGroupLabel className="brand-nav-label">Superuser</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {superAdminItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    {renderMenuItem(item)}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

          {hasFullCrmAccess && !isProductionRole && (
            <SidebarGroup>
              {!isCollapsed && <SidebarGroupLabel className="brand-nav-label">CRM</SidebarGroupLabel>}
              <SidebarGroupContent>
                <SidebarMenu>
                  {crmItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      {renderMenuItem(item)}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {hasBusinessPerformance && !isProductionRole && (
            <SidebarGroup>
              {!isCollapsed && <SidebarGroupLabel className="brand-nav-label">Business Performance</SidebarGroupLabel>}
              <SidebarGroupContent>
                <SidebarMenu>
                  {businessPerformanceItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      {renderMenuItem(item)}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

        {isPartner && !isProductionRole && (
          <SidebarGroup>
            {!isCollapsed && <SidebarGroupLabel className="brand-nav-label">Partner</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton 
                    asChild 
                    isActive={location.pathname === "/dashboard/partner-portal"}
                    className="brand-nav-item text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  >
                    <Link to="/dashboard/partner-portal" className="flex items-center gap-2">
                      <Handshake className="h-4 w-4" />
                      {!isCollapsed && <span>Partner Dashboard</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton 
                    asChild 
                    isActive={location.pathname === "/dashboard/partner-orders"}
                    className="brand-nav-item text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  >
                    <Link to="/dashboard/partner-orders" className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      {!isCollapsed && <span>Orders</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                 <SidebarMenuItem>
                   <SidebarMenuButton 
                     asChild 
                     isActive={location.pathname === "/dashboard/partner-resources"}
                     className="brand-nav-item text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                   >
                     <Link to="/dashboard/partner-resources" className="flex items-center gap-2">
                       <FileText className="h-4 w-4" />
                       {!isCollapsed && <span>Resources</span>}
                     </Link>
                   </SidebarMenuButton>
                 </SidebarMenuItem>
                 <SidebarMenuItem>
                   <SidebarMenuButton 
                     asChild 
                     isActive={location.pathname === "/dashboard/crm/contacts"}
                     className="brand-nav-item text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                   >
                     <Link to="/dashboard/crm/contacts" className="flex items-center gap-2">
                       <Users className="h-4 w-4" />
                       {!isCollapsed && <span>Contacts</span>}
                     </Link>
                   </SidebarMenuButton>
                 </SidebarMenuItem>
                 <SidebarMenuItem>
                   <SidebarMenuButton 
                     asChild 
                     isActive={location.pathname === "/dashboard/crm/companies"}
                     className="brand-nav-item text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                   >
                     <Link to="/dashboard/crm/companies" className="flex items-center gap-2">
                       <Building2 className="h-4 w-4" />
                       {!isCollapsed && <span>Organizations</span>}
                     </Link>
                   </SidebarMenuButton>
                 </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton 
                  onClick={handleLogout}
                  className="brand-nav-item text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground cursor-pointer"
                >
                  <LogOut className="h-4 w-4" />
                  {!isCollapsed && <span>Logout</span>}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}
